import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Constants, Globals } from 'utils';
import { AuthService, UserService } from 'services';
import { useBasics } from "../hooks/use-basics";
import { useDispatch } from "react-redux";

export const STEP = {
  STEP1: 1,
  STEP2: 2,
  STEP3: 3,
  STEP4: 4,
};

export const MODAL = {
  NONE: 0,
  SPECIALITY: 1,
  LANGUAGE: 2,
  ANIMAL: 3,
  SERVICE: 4,
  CATEGORY: 5,
  TERMS: 6,
};

export const useForm = () => {
  const nav = useHistory();
  const dispatch = useDispatch();
  const { form, getCities, modal, onChange, onChangeValue, selects, setModal, setStep, step } = useBasics(Constants.AFFILIATE_TYPES.BUSINESS_SERVICE_OR_ADVERTISER);

  const isValidForm = (showError = true) => {
    const onError = (msg: string) => {
      if (showError) Globals.showError(msg);
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // --- VALIDACIONES DEL PASO 1 ---
    if (step === STEP.STEP1) {
      // 1. Datos de la empresa
      if (!form.name || form.name.trim() === '') return onError('Debe indicar el nombre de la empresa');
      if (!form.document_type_id || !form.document) return onError('Debe indicar su tipo y número de documento');
      if (!form.birthdate) return onError('Debe indicar la fecha de fundación');
      if (!form.phonecode_id || !form.phone) return onError('Debe indicar su teléfono');
      if (!form.email) return onError('Debe indicar el correo electrónico de la empresa');
      else if (!emailRegex.test(form.email)) return onError('El correo de la empresa no tiene un formato válido');
      if (!form.state_id) return onError('Debe seleccionar un estado');
      if (!form.city_id) return onError('Debe seleccionar una ciudad');
      if (!form.address || form.address.trim() === '') return onError('Debe indicar la dirección de la empresa');

      // 2. Datos de la persona de contacto (Haciéndolos obligatorios)
      if (!form.contact_person_name || form.contact_person_name.trim() === '') return onError('Debe indicar el nombre de la persona de contacto');
      if (!form.contact_person_doc_type_id || !form.contact_person_document) return onError('Debe indicar el tipo y número de documento de la persona de contacto');
      if (!form.contact_person_phone_code_id || !form.contact_person_phone) return onError('Debe indicar el teléfono de la persona de contacto');
      if (!form.contact_person_email) return onError('Debe indicar el correo electrónico de la persona de contacto');
      else if (!emailRegex.test(form.contact_person_email)) return onError('El correo de la persona de contacto no tiene un formato válido');

      // 3. Contraseña y Términos
      if (!form.password || form.password.length < 8) return onError('La contraseña debe tener un mínimo de 8 caracteres');
      if (form.password !== form.password_confirmation) return onError('Las contraseñas no coinciden');
      if (!form.terms) return onError('Debe aceptar los términos y condiciones');
    }
    // --- VALIDACIONES DEL PASO 2 ---
    if (step === STEP.STEP2) {
      if (!form.categories || form.categories.length === 0)
        return onError('Debe agregar al menos una categoría');

      if (!form.consultation?.init_hour)
        return onError('Debe indicar la hora de inicio de consulta');

      if (!form.consultation?.end_hour)
        return onError('Debe indicar la hora de fin de consulta');

      // Validar coherencia de horas
      if (moment(form.consultation.init_hour, 'HH:mm:ss').isAfter(moment(form.consultation.end_hour, 'HH:mm:ss')))
        return onError('La hora de inicio no puede ser posterior a la hora de fin de consulta');

      if (!form.consultation?.days || form.consultation.days.length === 0)
        return onError('Debe seleccionar al menos un día de atención');

      if (!form.consultation?.contact_methods || form.consultation.contact_methods.length === 0)
        return onError('Debe seleccionar al menos un método de contacto');
    }

    // --- VALIDACIONES DEL PASO 3 ---
    if (step === STEP.STEP3) {
      if (form.products && form.products.length > 0) {
        if (!form.products.every((i: any) => i.name))
          return onError('Debe indicar el nombre de todos los productos agregados');

        if (!form.products.every((i: any) => i.image))
          return onError('Debe subir una foto de todos los productos agregados');
      }
    }

    // --- VALIDACIONES DEL PASO 4 ---
    if (step === STEP.STEP4) {
      if (!form.description || form.description.trim() === '') {
        return onError('Debe indicar una descripción para su empresa');
      }

      if (!form.terms_truth || !form.terms_verify || !form.terms_law) {
        return onError('Debe aceptar todas las declaraciones y condiciones para finalizar');
      }
    }

    return true;
  }

  const parseOutput = () => {
    const name = [form.name, form.lastname || ''].join(' ').trim();

    const consultation: any = Globals.clone(form.consultation);
    consultation.init_hour = form.consultation.init_hour || '';
    consultation.end_hour = form.consultation.end_hour || '';
    consultation.days = (form.consultation.days || []).join(',');
    consultation.teleconsultation_rate_id = null;

    const productFiles: any = {};
    if (form.products.length) {
      form.products.forEach((item: any, index: number) => {
        if (item.image_blob)
          productFiles[`products[${index}][image]`] = item.image;
      });
    }

    return {
      ...form,
      ...productFiles,
      name,
      categories: JSON.stringify(form.categories.map((i: any) => Number(i))),
      consultation: JSON.stringify({
        ...form.consultation,
        init_hour: form.consultation.init_hour ? moment(form.consultation.init_hour, 'HH:mm:ss').format('hh:mm A') : '',
        end_hour: form.consultation.end_hour ? moment(form.consultation.end_hour, 'HH:mm:ss').format('hh:mm A') : '',
        days: (form.consultation.days || []).join(','),
      }),
      products: JSON.stringify(form.products.map((i: any) => ({ name: i.name, image: i.image?.value || i.image || '' }))),
      veterinarians: JSON.stringify(form.veterinarians),
      photo: form.photo?.value || '',
      hasFile: true,
    };
  }

  const onSubmit = async () => {
    try {
      // Crea el usuario
      const payload = parseOutput();
      await UserService.admin.createOrUpdate(payload);

      // Inicia sesión
      const res: any = await AuthService.login({
        email: payload.email,
        password: payload.password,
        withoutError: true,
      });
      Globals.showSuccess('Su cuenta ha sido creada correctamente');
      dispatch({ type: 'SET_USER', payload: res.user });
      nav.replace('/');

    } catch (error) {
      const err: any = error;
      const message = err?.response?.data?.error || 'Error al crear la cuenta';
      Globals.showError(message);
      console.log(error);
    }
  }

  const prevStep = () => {
    setStep((s: number) => s - 1);
  }

  const nextStep = () => {
    // 1. PRIMERO validamos que el paso actual cumpla las reglas
    if (!isValidForm()) return;

    // 2. Si todo está correcto y no es el último paso, avanzamos
    if (step < Math.max(...Object.values(STEP))) {
      return setStep((s: number) => s + 1);
    }

    // 3. Si es el último paso y todo es válido, enviamos al backend
    onSubmit();
  }

  return {
    form,
    getCities,
    modal,
    nextStep,
    onChange,
    onChangeValue,
    onSubmit,
    prevStep,
    selects,
    setModal,
    step,
  }
}